import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// Mounts on gives/collections/show (and custom), the "Choose a Gift Collection" marketplace, and
// on subdomain/collections/show, the org-dashboard collection detail page.
const JQUERY_NAMESPACE = ".giveCollection"

const LOADING_PRODUCT_DETAIL_HTML = `
  <div class="product-details-loading">
    <div class="loading-images-column">
      <div class="loading-thumbs">
        <div class="loading-thumb product-modal-shimmer"></div>
        <div class="loading-thumb product-modal-shimmer"></div>
        <div class="loading-thumb product-modal-shimmer"></div>
      </div>
      <div class="loading-fullsize product-modal-shimmer"></div>
    </div>
    <div class="loading-info-column">
      <div class="loading-title product-modal-shimmer"></div>
      <div class="loading-tags product-modal-shimmer"></div>
      <div class="loading-summary product-modal-shimmer"></div>
      <div class="loading-line product-modal-shimmer"></div>
      <div class="loading-select product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
    </div>
  </div>
`;
const SLICK_ARROW_LEFT = "<button type=\"button\" class=\"slick-prev slick-arrow pill\"><i aria-hidden=\"true\" class=\"icon-lasso-chevron-left\"></i></button>";
const SLICK_ARROW_RIGHT = "<button type=\"button\" class=\"slick-next slick-arrow pill\"><i aria-hidden=\"true\" class=\"icon-lasso-chevron-right\"></i></button>";

// The product detail modal. collection_detail_products_controller.js and the collection
// generator carry their own equivalents rather than sharing one module.
class ProductDetailModal {
  constructor({ modalEl, products, buildDetailUrl }) {
    this.modalEl = modalEl;
    this.carouselEl = modalEl ? modalEl.querySelector(".product-details-carousel") : null;
    this.products = products;
    this.buildDetailUrl = buildDetailUrl;
    this.currentProductIndex = 0;

    if (this.modalEl) {
      $(this.modalEl).on(`shown.bs.modal${JQUERY_NAMESPACE}`, () => this.onShown());
      $(this.modalEl).on(`hidden.bs.modal${JQUERY_NAMESPACE}`, () => this.onHidden());
    }
  }

  destroy() {
    if (this.modalEl) { $(this.modalEl).off(JQUERY_NAMESPACE); }
    if (this.carouselEl && $(this.carouselEl).hasClass("slick-initialized")) {
      $(this.carouselEl).off("afterChange");
      $(this.carouselEl).slick("unslick");
    }
  }

  open(index) {
    this.currentProductIndex = isNaN(index) ? 0 : index;
    $(this.modalEl).modal("show");
  }

  // When the modal shows, load the carousel and fetch the product details
  onShown() {
    const $carousel = $(this.carouselEl);
    $carousel.empty();
    this.products.forEach(() => $carousel.append("<div class=\"item\"></div>"));

    $carousel.slick({
      infinite: false,
      initialSlide: this.currentProductIndex,
      arrows: true,
      nextArrow: SLICK_ARROW_RIGHT,
      prevArrow: SLICK_ARROW_LEFT,
      responsive: [
        {
          // Mobile
          breakpoint: 768,
          settings: { arrows: false }
        }
      ]
    });

    // When the carousel changes, load the other slide content.
    // Rebound on every open since it's unbound in `onHidden` below; `.off` first guards
    // against double-binding if this ever runs twice per open.
    $carousel.off("afterChange").on("afterChange", (event, slick, currentSlide) => {
      this.currentProductIndex = currentSlide;
      this.insertProductDetails();
      // This page never paginates (totalPages is always 1) so this branch can never fire -
      // preserved as-is, a long-standing guard.
    });

    this.insertProductDetails();
  }

  // Resets the carousel when the modal is hidden
  onHidden() {
    const $carousel = $(this.carouselEl);
    if ($carousel.hasClass("slick-initialized")) {
      $carousel.off("afterChange");
      $carousel.slick("unslick");
    }
  }

  insertProductDetails(productIndex = this.currentProductIndex) {
    const $slide = $(this.carouselEl).find(".slick-slide").not(".slick-cloned").eq(productIndex);
    this.fetchProductDetailIntoSlide($slide, this.products[productIndex]);
  }

  // Fetch the product from the BE and insert the HTML into the slide.
  //
  // S4, accepted at both sinks. The placeholder is LOADING_PRODUCT_DETAIL_HTML, the module
  // constant above -- a fixed shimmer skeleton with nothing interpolated into it, so it is not a
  // surface at all. The fetched body is server-rendered HTML from our own origin:
  // `product.detail_url` comes from the declared `products` value the view writes, and the
  // response is gives/collections/products/_modal_body.html.haml, rendered by
  // Gives::Collections::ProductsController#show. Read it: every field uses plain HAML `=` except
  // .summary/.description, which go through ProductDecorator -> ProductsHelper#sanitized_html,
  // which calls Rails' own `sanitize`; no `raw`/`html_safe`/`!=` and no `:javascript` block, which
  // matters because jQuery's `.append()` here would execute an embedded script. This is the same
  // partial and the same audit as collection_detail_products_controller.js's modal sink.
  fetchProductDetailIntoSlide($slide, product) {
    if (!product || $slide.data("loaded")) {
      return;
    }

    $slide.html(LOADING_PRODUCT_DETAIL_HTML);

    fetch(this.buildDetailUrl(product), RequestHelper.defaultRequestOptions())
      .then((response) => response.text())
      .then((html) => {
        $slide.empty().append(html);
        this.wireProductDetail($slide);
        $slide.data("loaded", true);
      })
      .catch((error) => console.warn("Failed to load product detail into slide", error)); // eslint-disable-line no-console
  }

  // gives/collections/products/_modal_body is shared with the collection detail and generator
  // views and carries no data-action of its own, so its controls are wired up by hand here.
  wireProductDetail($slide) {
    $slide.find(".thumbs a").on("click", (event) => this.scrollToCorrespondingImg(event));
    $slide.find(".variants select").on("change", (event) => this.changeVariant(event.target.value));
  }

  /* Product details modal - scrolling */

  changeVariant(id) {
    const isDesktop = window.innerWidth > 1130;

    const variant = isDesktop ? "desktop" : "mobile";
    const scrollAttr = isDesktop ? "scrollTop" : "scrollLeft";
    const prop = isDesktop ? "top" : "left";

    const $modal = $(this.modalEl);
    const $thumbnails = $modal.find(`.slick-active .product-details-${variant} .thumbs`);
    const $fullSizeImages = $modal.find(`.slick-active .product-details-${variant} .fullsize`);

    [$thumbnails, $fullSizeImages].forEach((container) => {
      if (!container.length) {
        return;
      }

      const img = container.find(`img[data-id="${id}"]`)[0];

      if (img) {
        container[0][scrollAttr] =
          $(img).offset()[prop] - container.offset()[prop] + container[0][scrollAttr];
      } else {
        container[0][scrollAttr] = 0;
      }
    });

    $thumbnails.find("a").removeClass("active");
    const activeThumb = $thumbnails.find(`img[data-id="${id}"]`)[0];

    if (activeThumb) {
      $(activeThumb).parent().addClass("active");
    } else {
      $thumbnails.find("a:first").addClass("active");
    }
  }

  scrollToCorrespondingImg(event) {
    event.preventDefault();

    const isDesktop = window.innerWidth > 1130;
    const src = $(event.target).attr("src").split("tile")[0];

    const variant = isDesktop ? "desktop" : "mobile";
    const scrollAttr = isDesktop ? "scrollTop" : "scrollLeft";
    const prop = isDesktop ? "top" : "left";

    const $modal = $(this.modalEl);
    const $thumbnails = $modal.find(`.slick-active .product-details-${variant} .thumbs`);
    const $fullSizeImages = $modal.find(`.slick-active .product-details-${variant} .fullsize`);
    const img = $fullSizeImages.find(`img[src^="${src}"]`)[0];

    $thumbnails.find("a").removeClass("active");
    $(event.target).parent().addClass("active");

    if (!$fullSizeImages.length) {
      return;
    }

    if (img) {
      $fullSizeImages[0][scrollAttr] =
        $(img).offset()[prop] - $fullSizeImages.offset()[prop] + $fullSizeImages[0][scrollAttr];
    } else {
      $fullSizeImages[0][scrollAttr] = 0;
    }
  }
}

export default class extends Controller {
  static get values() {
    return { collections: Array, customPackagingBundle: String, products: Array };
  }

  connect() {
    // Dead: nothing on the views this controller mounts on calls selectCollection, the only
    // reader of collectionIds/currentIdx.
    const buildCollectionIdField = this.element.querySelector("#build_collection_id");
    this.collectionIds = this.collectionsValue;
    this.currentCollectionId = parseInt(buildCollectionIdField ? buildCollectionIdField.value : undefined, 10);
    this.currentIdx = this.collectionIds.indexOf(this.currentCollectionId);

    // Product details modal requirements
    this.products = this.productsValue;
    this.currentPage = 1;
    this.totalPages = 1;
    this.totalProducts = this.products.length;

    this.productDetailModal = new ProductDetailModal({
      modalEl: this.element.querySelector("#product-detail-modal"),
      products: this.products,
      buildDetailUrl: (product) => product.detail_url
    });

    // Known quirk: `.collection-page` renders on none of this controller's views, so this always
    // evaluates falsy and always hides the indicator, whatever the server rendered.
    this.displayPlatformCollectionIndicator(this.isPlatformCollection());

    // Dead: no `#search` input or `.search-component` renders on any view. Binding to an empty
    // jQuery set is a harmless no-op.
    this.$search = $("#search");
    this.searchChanged = false;

    this.searchChangeHandler = () => { this.searchChanged = true; };
    this.searchBlurHandler = (e) => {
      setTimeout(() => {
        if (this.searchChanged) {
          this.filterUpdated(e);
          this.searchChanged = false;
        } else if (e.originalEvent.explicitOriginalTarget.id != "clear-search") {
          this.deactivateSearch();
        }
      }, 200);
    };
    this.searchKeypressHandler = (e) => {
      if (e.which === 13) {
        this.filterUpdated(e);
      }
    };

    this.$search.on(`change${JQUERY_NAMESPACE}`, this.searchChangeHandler);
    this.$search.on(`blur${JQUERY_NAMESPACE}`, this.searchBlurHandler);
    this.$search.on(`keypress${JQUERY_NAMESPACE}`, this.searchKeypressHandler);

    this.collectionCardClickHandler = (e) => {
      // S3: the card's `data-url` is a whole destination taken wholesale out of markup, so it is
      // asserted same-origin before navigating even though the partials only ever render
      // `give_collection_preview_path` into it. This is a plain click-through with no error UI of
      // its own, so the failing branch simply does not navigate.
      const destination = $(e.target).closest(".collection-card").data("url") + window.location.search;
      if (RequestHelper.isSameOriginUrl(destination)) {
        window.location.href = destination;
      }
    };
    this.collectionCardLinkClickHandler = (e) => { e.stopPropagation(); };
    this.returnLinkClickHandler = (e) => {
      e.preventDefault();
      const querystring = new URLSearchParams(window.location.search);
      // Remove the category param from the querystring to avoid preselecting categories in collection previews
      querystring.delete("category");
      const questionMarkOrEmpty = querystring.toString() === "" ? "" : "?";
      // S3: same shape as the card click above - the anchor's own `href` is the whole destination,
      // so it is asserted same-origin before this handler navigates on the link's behalf. The
      // handler has already called preventDefault, so the failing branch leaves the click inert
      // rather than sending the user off-origin.
      const destination = `${$(e.currentTarget).attr("href")}${questionMarkOrEmpty}${querystring.toString()}`;
      if (RequestHelper.isSameOriginUrl(destination)) {
        window.location.href = destination;
      }
    };
    this.teamsMenuClickHandler = () => {
      $(this.element).find(".filter-sidebar").removeClass("active");
      $(this.element).find(".mobile-filter-background").removeClass("active");
    };

    $(".gives-content .collection-card :not(.send, .slick-arrow, .slick-arrow i)").on(`click${JQUERY_NAMESPACE}`, this.collectionCardClickHandler);
    $(".collection-card a").on(`click${JQUERY_NAMESPACE}`, this.collectionCardLinkClickHandler);
    $("a.return-link").on(`click${JQUERY_NAMESPACE}`, this.returnLinkClickHandler);
    $(".teams-menu").on(`click${JQUERY_NAMESPACE}`, this.teamsMenuClickHandler);
  }

  disconnect() {
    this.productDetailModal.destroy();

    this.$search.off(JQUERY_NAMESPACE);
    $(".gives-content .collection-card :not(.send, .slick-arrow, .slick-arrow i)").off(JQUERY_NAMESPACE, this.collectionCardClickHandler);
    $(".collection-card a").off(JQUERY_NAMESPACE, this.collectionCardLinkClickHandler);
    $("a.return-link").off(JQUERY_NAMESPACE, this.returnLinkClickHandler);
    $(".teams-menu").off(JQUERY_NAMESPACE, this.teamsMenuClickHandler);

    clearTimeout(this.mobileFiltersOpenTimer);
    clearTimeout(this.mobileFiltersCloseTimer);
  }

  nextStep(event) {
    event.preventDefault();
    const $target = $(event.currentTarget);
    const collectionId = $target.data("collection");
    const requireCustomPackaging = $target.data("require-custom");
    const buildCustomPackagingBundleId = this.customPackagingBundleValue;
    const $bundleModal = $("#custom-packaging-bundle-modal");
    const $form = $("form#giving-form");

    // Custom packaging collections need a bundle -- prompt the user to pick one
    if (requireCustomPackaging && !buildCustomPackagingBundleId && $bundleModal.length > 0) {
      $bundleModal.modal("show");
      return false;
    }

    if (collectionId) {
      $form.find("#build_collection_id").val(collectionId);
    }

    if (buildCustomPackagingBundleId) {
      $form.find("#build_custom_packaging_bundle_id").val(buildCustomPackagingBundleId);
    }

    $form.submit();
  }

  selectCollection(selection) {
    const collectionId = selection.data("collectionId");
    this.currentIdx = this.collectionIds.indexOf(parseInt(collectionId, 10));
    $(this.element).find("form#giving-form input#build_collection_id").val(collectionId);
  }

  setDropdownSelection(event) {
    const selection = $(event.currentTarget).find(".gift-collection");
    $(this.element).find(".collection-dropdown-text").text(selection.text());
    $(this.element).find(".dropdown-menu li").removeClass("selected");
    selection.parents("li").addClass("selected");
  }

  displayPlatformCollectionIndicator(display) {
    const $platformCollectionIndicator = $(".platform-collection-indicator");

    if (display) {
      $platformCollectionIndicator.removeClass("hide");
    } else {
      $platformCollectionIndicator.addClass("hide");
    }
  }

  isPlatformCollection() {
    return $(".collection-page").data("is-platform-collection");
  }

  expandProductDetails(event) {
    if ($(event.target).closest(".slick-arrow").length) {
      return;
    }

    // Unlike the products-grid pattern this is based on, the trigger here is a real
    // <a href> (kept for no-JS fallback/right-click), so its default navigation must
    // be suppressed or the browser follows the link after the modal opens.
    event.preventDefault();

    const index = parseInt($(event.currentTarget).data("productIndex"));
    this.productDetailModal.open(index);
  }

  // Rebuilds the querystring from the currently-active filter pills (+ search)
  // and navigates. Active pills carry the filter param on their `id`; the
  // "All Price Points" pill has no id, so it never contributes a param.
  applyFilters() {
    const parts = [];
    $(".filter-pill.active").each((idx, pill) => {
      if (pill.id) {
        parts.push(`${pill.id}=1`);
      }
    });
    const search = $("#search").val();
    if (search) {
      parts.push(`search=${encodeURIComponent(search)}`);
    }
    const searchString = parts.length ? `?${parts.join("&")}` : "";
    if (searchString || window.location.search) {
      window.location.search = searchString;
    }
  }

  // Legacy name kept for the search input handlers above.
  filterUpdated(event) {
    this.applyFilters();
  }

  // Keeps the "All Price Points" pill active iff no specific price pill is selected.
  syncAllPricePoints() {
    const anyPriceActive = $(".price-pills .filter-pill[id].active").length > 0;
    $(".price-pills .all-price-points").toggleClass("active", !anyPriceActive);
  }

  togglePill(event) {
    event.preventDefault();
    $(event.currentTarget).toggleClass("active");
    this.syncAllPricePoints();
    this.applyFilters();
  }

  clearPriceFilters(event) {
    event.preventDefault();
    $(".price-pills .filter-pill").removeClass("active");
    $(".price-pills .all-price-points").addClass("active");
    this.applyFilters();
  }

  clearSearchInput(event) {
    event.preventDefault();

    if (this.$search.val() === "") {
      this.deactivateSearch();
    } else {
      this.$search.val("");
      this.$search.focus();
      this.searchChanged = true;
    }
  }

  showMobileFilters(event) {
    event.preventDefault();

    $("html, body").css({ height: "100%", overflow: "hidden" });
    $(this.element).find(".mobile-filter-background").addClass("active");
    $(this.element).find(".filter-sidebar").addClass("is-mobile");

    this.mobileFiltersOpenTimer = setTimeout(() => {
      $(this.element).find(".filter-sidebar").addClass("opening");
    }, 10);
  }

  hideMobileFilters(event) {
    event.preventDefault();

    $("html, body").css({ height: "auto", overflow: "auto" });
    $(this.element).find(".filter-sidebar").addClass("closing");

    this.mobileFiltersCloseTimer = setTimeout(() => {
      $(this.element).find(".mobile-filter-background").removeClass("active");
      $(this.element).find(".filter-sidebar").removeClass("is-mobile");
      $(this.element).find(".filter-sidebar").removeClass("closing");
      $(this.element).find(".filter-sidebar").removeClass("opening");
    }, 1000);
  }

  activateSearch() {
    $(this.element).find(".filter-sidebar .heading").animate({ opacity: "0" }, 500);
    $(this.element).find(".filter-sidebar .search-component").addClass("active");

    this.$search.focus();
  }

  deactivateSearch() {
    $(this.element).find(".filter-sidebar .heading").animate({ opacity: "1" }, 500);

    $(".filter-sidebar .search-component").removeClass("active");
  }
}
